import { Injectable } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { Capacitor } from '@capacitor/core';

import { IContentCrashEvent } from '../../enums/Crashlytics';

@Injectable({
  providedIn: 'root'
})
export class CrashlyticsService {

  private isMovil = false;
  private isAndroid = false;

  constructor() {
    this.isMovil = Capacitor.isNativePlatform();
    this.isAndroid = Capacitor.getPlatform() === 'android';
  }

  /**
   * Activar/desactivar manualmente la recopilación de datos de Crashlytics,
   * p. si está deshabilitado al iniciar la aplicación.
   * Parameters:
   * @param {boolean} enabled - si habilitar o deshabilitar la recopilación de datos de Crashlytics.
   */
  public setCrashlyticsCollectionEnabled = async (enabled: boolean): Promise<boolean> => {
    if (!this.isMovil) {
      return false;
    }

    try {
      await FirebaseCrashlytics.setEnabled({ enabled });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Establecer el identificador de usuario de Crashlytics.
   *
   * Para diagnosticar un problema, a menudo es útil saber cuál de sus usuarios experimentó un bloqueo determinado.
   * Crashlytics incluye una forma de identificar de forma anónima a los usuarios en sus informes de fallos.
   * Para agregar ID de usuario a sus informes, asigne a cada usuario un identificador único en forma de número de ID, token o valor hash.
   *
   * Ver {@link https://firebase.google.com/docs/crashlytics/customize-crash-reports?authuser=0#set_user_ids Firebase docs} para más información.
   *
   * Parameters:
   * @param {string} userId - ID de usuario para asociar con los informes de Crashlytics
   */
  public setCrashlyticsUserId = async (userId: string): Promise<boolean> => {
    if (!this.isMovil) {
      return false;
    }

    try {
      await FirebaseCrashlytics.setUserId({ userId });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Envía un mensaje de registro relacionado con el bloqueo que aparecerá en la sección Logs del próximo evento de bloqueo nativo.
   * Note: Si no falla, ¡el mensaje no se enviará! También registra el mensaje en la consola del dispositivo nativo.
   *
   * Parameters:
   * @param {string} message - mensaje para asociar con el próximo evento de bloqueo nativo
   */
  public logMessage = async (message: string): Promise<boolean> => {
    if (!this.isMovil) {
      return false;
    }

    try {
      await FirebaseCrashlytics.log({ message });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Envía un evento de error non-fatal a Crashlytics.
   * En una aplicación de Cordova, puede usar esto para registrar excepciones de Javascript no controladas, por ejemplo.
   * * El evento aparecerá en Event type = "Non-fatals" en la consola de Crashlytics.
   * * El mensaje de error aparecerá en la sección Logs del evento de error non-fatal.
   * * También registra el mensaje de error en la consola del dispositivo nativo.
   *
   * Parameters:
   * @param {string} errorMessage - non-fatal mensaje de error para iniciar sesión en Crashlytics
   * @param {object} stackTrace - (optional) un stack trace generado por stacktrace.js
   */
  public logError = async <T>(errorMessage: string, stackTrace?: object | T): Promise<boolean> => {
    if (!this.isMovil) {
      return false;
    }

    try {
      // Registrar el mensaje de error
      await FirebaseCrashlytics.log({ message: errorMessage });

      // Convertir el objeto stackTrace en un formato compatible con StackFrame[]
      const stackFrames = this.convertToStackFrames(stackTrace);

      // Registrar la excepción como un error no fatal
      await FirebaseCrashlytics.recordException({
        message: errorMessage,
        stacktrace: stackFrames
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Convierte un objeto de stackTrace a un formato compatible con StackFrame[]
   * @param stackTrace Objeto que contiene la información de stack trace
   * @returns Array de StackFrame compatible con Crashlytics
   */
  private convertToStackFrames<T>(stackTrace?: object | T): Array<{ lineNumber?: number, fileName?: string, functionName?: string }> {
    if (!stackTrace) {
      return [{
        fileName: 'unknown',
        functionName: 'unknown',
        lineNumber: 0
      }];
    }

    try {
      // Si es un array de errores, intentamos convertirlo directamente
      if (Array.isArray(stackTrace)) {
        return stackTrace.map(frame => ({
          lineNumber: frame.lineNumber || 0,
          fileName: frame.fileName || 'unknown',
          functionName: frame.functionName || 'unknown'
        }));
      }

      // Si es un objeto Error o similar, extraemos lo que podamos
      const stackString = JSON.stringify(stackTrace);
      return [{
        fileName: 'manual-report',
        functionName: stackString.substring(0, 500), // Limitar longitud
        lineNumber: 1
      }];
    } catch (e) {
      return [{
        fileName: 'conversion-error',
        functionName: 'Failed to convert stack trace',
        lineNumber: 0
      }];
    }
  }

  /**
   * Envía un mensaje de registro relacionado con el bloqueo que aparecerá en la sección Logs del próximo evento de bloqueo nativo.
   * y envía un evento de error non-fatal a Crashlytics.
   * @param {IContentCrashEvent} eventCrash - propiedades del evento del bloqueo
   * @param error - un stacktrace error a enviar a consola *Solo Android
   */
  public sendCrashPlatform = async <T>(eventCrash: IContentCrashEvent, error: object | T): Promise<boolean> => {
    const key = `${eventCrash.tipo}-${eventCrash.modulo}-${eventCrash.funcion}-${eventCrash.identificador}`;
    await this.logMessage(key);
    return await this.logError(key, error);
  }

}
